import { useCallback, useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { getEmployeeDirectory, type EmployeeDirectoryItem } from '../api/employees';
import { Card, CardContent } from '../components/ui/card';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import PageHeader from '../components/ui/PageHeader';
import { cn } from '@/lib/utils';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function Employees() {
  const [employees, setEmployees] = useState<EmployeeDirectoryItem[]>([]);
  const [clockedInCount, setClockedInCount] = useState(0);
  const [onLeaveCount, setOnLeaveCount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await getEmployeeDirectory();
    if (res.data?.data) {
      setEmployees(res.data.data);
      setClockedInCount(res.data.meta?.clockedInCount ?? 0);
      setOnLeaveCount(res.data.meta?.onLeaveCount ?? 0);
    } else if (res.error) {
      setError(res.error);
      setEmployees([]);
      setClockedInCount(0);
      setOnLeaveCount(0);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 60_000);
    return () => window.clearInterval(interval);
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="All employees"
        subtitle={
          employees.length > 0
            ? `${clockedInCount} clocked in · ${onLeaveCount} on leave`
            : 'Company directory'
        }
      />

      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <LoadingState label="Loading employees…" />
      ) : employees.length === 0 ? (
        <EmptyState icon={Users} title="No employees found" />
      ) : (
        <ul className="space-y-2">
          {employees.map((emp) => (
            <li key={emp.id}>
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="relative shrink-0">
                    <span
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium',
                        emp.isClockedIn
                          ? 'bg-success/10 text-success ring-2 ring-success/40'
                          : emp.isOnLeave
                            ? 'bg-warning/10 text-warning ring-2 ring-warning/30'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {initials(emp.fullName)}
                    </span>
                    {emp.isClockedIn && (
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background bg-success" />
                    )}
                    {!emp.isClockedIn && emp.isOnLeave && (
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background bg-warning" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'font-semibold',
                        emp.isClockedIn
                          ? 'text-success'
                          : emp.isOnLeave
                            ? 'text-warning'
                            : 'text-foreground'
                      )}
                    >
                      {emp.fullName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {emp.employeeNumber ? `${emp.employeeNumber} · ` : ''}
                      {emp.email}
                      {emp.department ? ` · ${emp.department}` : ''}
                    </p>
                    {emp.isOnLeave && !emp.isClockedIn && (
                      <p className="mt-1 text-xs font-medium text-warning">
                        {emp.leaveLabel ?? 'On leave'}
                        {emp.leaveEndDate
                          ? ` · until ${new Date(`${emp.leaveEndDate}T00:00:00`).toLocaleDateString('en-MY', {
                              day: 'numeric',
                              month: 'short',
                            })}`
                          : ''}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
